/**
 * useUrl — a tiny hook that re-renders the caller when the browser URL
 * changes (back/forward, or programmatic `pushState`). Returns the current
 * pathname + search so callers can re-derive route state.
 *
 * To make `history.pushState` observable we patch it once at module load
 * to dispatch a custom `urlchange` event. (`pushState` itself doesn't fire
 * `popstate` — only back/forward do.) `replaceState` gets the same patch.
 *
 * Single dispatch rule reminder: this hook is only ever called from App.tsx
 * and from useNavTree (both inside the v2 tree). Legacy pages don't import
 * it.
 */
import { useSyncExternalStore } from 'react';

const URLCHANGE = 'voxcore:urlchange';

let patched = false;
function patchHistory() {
  if (patched || typeof window === 'undefined') return;
  patched = true;
  const orig = window.history.pushState.bind(window.history);
  window.history.pushState = function (...args: Parameters<typeof window.history.pushState>) {
    orig(...args);
    window.dispatchEvent(new Event(URLCHANGE));
  };
  const origReplace = window.history.replaceState.bind(window.history);
  window.history.replaceState = function (...args: Parameters<typeof window.history.replaceState>) {
    origReplace(...args);
    window.dispatchEvent(new Event(URLCHANGE));
  };
}

function subscribe(cb: () => void) {
  patchHistory();
  window.addEventListener(URLCHANGE, cb);
  window.addEventListener('popstate', cb);
  return () => {
    window.removeEventListener(URLCHANGE, cb);
    window.removeEventListener('popstate', cb);
  };
}

function snapshot(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search + window.location.hash;
}

function serverSnapshot(): string { return '/'; }

// When the v2 React Router is active it registers itself here so `navigate()`
// routes through it (the router won't observe a raw history.pushState). Kept as
// a registration hook — not a direct import — to avoid a cycle (router.tsx
// imports the v2 pages, which import this module). Falls back to the pushState
// behavior below before the router is registered (and on the legacy path).
interface RouterLike { navigate: (to: string, opts?: { replace?: boolean }) => void | Promise<void>; }
let routerInstance: RouterLike | null = null;
export function registerRouter(router: RouterLike) {
  routerInstance = router;
}

/** Programmatically navigate within the SPA. Routes through the v2 router when
 *  registered; otherwise uses `pushState` so the URL updates without a reload
 *  (the patched `pushState` notifies subscribers). */
export function navigate(url: string, opts: { replace?: boolean } = {}) {
  if (typeof window === 'undefined') return;
  if (routerInstance) {
    void routerInstance.navigate(url, opts);
    return;
  }
  patchHistory();
  if (opts.replace) window.history.replaceState({}, '', url);
  else window.history.pushState({}, '', url);
}

/** Re-renders the caller whenever the URL changes. Returns the current
 *  `pathname + search + hash` — caller passes that to whatever route helper
 *  it uses (e.g. `getUrlParams()`). */
export function useUrl(): string {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}
